import React from 'react';
import { ArrowLeft, Play, Calculator, ArrowRight, Shield, Zap } from 'lucide-react';

interface DemoVideoProps {
  onNavigateBack: () => void;
  onStartProfilRisque: () => void;
}

const DemoVideo: React.FC<DemoVideoProps> = ({ onNavigateBack, onStartProfilRisque }) => {

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={onNavigateBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Retour</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Play className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-xl font-bold text-gray-900">Démonstration SOeasi</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Titre principal */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-gray-900">Comment fonctionne</span>
            <br />
            <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">SOeasi ?</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            Découvrez en 3 minutes comment notre plateforme révolutionne l'investissement éthique et accessible
          </p>
        </div>

        {/* Vidéo principale */}
        <div className="mb-16">
          <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl overflow-hidden shadow-2xl relative">
            {/* Placeholder pour la vraie vidéo */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white space-y-6">
                <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto backdrop-blur-sm hover:bg-white/30 transition-all duration-300 cursor-pointer">
                  <Play className="w-12 h-12 text-white ml-1" />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2">Vidéo explicative SOeasi</h3>
                  <p className="text-white/80 text-lg">Durée: 3:30 minutes</p>
                </div>
              </div>
            </div>

            {/* Overlay avec timeline */}
            <div className="absolute bottom-8 left-8 right-8">
              <div className="bg-white/95 backdrop-blur-md rounded-2xl p-6">
                <div className="text-sm text-gray-900 font-semibold mb-4">Plan de la vidéo :</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">1</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Présentation</div>
                      <div className="text-xs text-gray-600">0:00 - 0:45</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">2</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Analyse besoins</div>
                      <div className="text-xs text-gray-600">0:45 - 1:30</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">3</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Investissements</div>
                      <div className="text-xs text-gray-600">1:30 - 2:45</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">4</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Suivi & résultats</div>
                      <div className="text-xs text-gray-600">2:45 - 3:30</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Parcours explicatifs détaillés */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Parcours Accompagné */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-100/50 shadow-lg">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Parcours Accompagné</h3>
                <p className="text-gray-600">Pour les investisseurs qui préfèrent être guidés</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-2">Analyse complète de vos besoins</div>
                  <div className="text-gray-600 mb-3">Questionnaire détaillé sur vos objectifs financiers, votre situation personnelle et vos préférences d'investissement.</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-teal-100 text-teal-700 px-2 py-1 rounded-md text-xs">Objectifs financiers</span>
                    <span className="bg-teal-100 text-teal-700 px-2 py-1 rounded-md text-xs">Horizon temporel</span>
                    <span className="bg-teal-100 text-teal-700 px-2 py-1 rounded-md text-xs">Capacité d'épargne</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-2">Évaluation de votre profil investisseur</div>
                  <div className="text-gray-600 mb-3">Tests scientifiques pour déterminer votre tolérance au risque et vos préférences comportementales.</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs">Test de risque</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs">Profil comportemental</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-md text-xs">Préférences éthiques</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-2">Préconisations personnalisées</div>
                  <div className="text-gray-600 mb-3">Notre IA génère une stratégie d'allocation optimale adaptée à votre profil unique.</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-md text-xs">Allocation optimale</span>
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-md text-xs">Suivi automatique</span>
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-md text-xs">Rééquilibrage</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Investissement Direct */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-100/50 shadow-lg">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Investissement Direct</h3>
                <p className="text-gray-600">Pour les investisseurs autonomes et expérimentés</p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-2">Parcourir notre marketplace</div>
                  <div className="text-gray-600 mb-3">Explorez nos opportunités d'investissement vérifiées : immobilier, Sukuk, et produits éthiques.</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-md text-xs">Immobilier</span>
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-md text-xs">Sukuk</span>
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-md text-xs">Forêts</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-2">Analyser et choisir</div>
                  <div className="text-gray-600 mb-3">Consultez les fiches détaillées, les performances historiques et les projections de chaque projet.</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-pink-100 text-pink-700 px-2 py-1 rounded-md text-xs">Due diligence</span>
                    <span className="bg-pink-100 text-pink-700 px-2 py-1 rounded-md text-xs">Analyses financières</span>
                    <span className="bg-pink-100 text-pink-700 px-2 py-1 rounded-md text-xs">Certifications</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-2">Investir en quelques clics</div>
                  <div className="text-gray-600 mb-3">Processus d'investissement simplifié avec suivi en temps réel de vos performances.</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-md text-xs">Investissement rapide</span>
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-md text-xs">Suivi temps réel</span>
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-md text-xs">Reporting détaillé</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action final */}
        <div className="text-center bg-white/80 backdrop-blur-sm rounded-3xl p-12 border border-gray-100/50 shadow-lg">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Prêt à commencer votre parcours ?</h3>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Choisissez l'approche qui vous convient le mieux et rejoignez des milliers d'investisseurs qui font confiance à SOeasi
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              onClick={onStartProfilRisque}
              className="bg-gradient-to-r from-teal-600 to-blue-600 text-white px-10 py-5 rounded-2xl font-bold text-xl hover:from-teal-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-4 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              <Calculator className="w-7 h-7" />
              <span>Commencer mon analyse personnalisée</span>
            </button>
            
            <button
              onClick={onNavigateBack}
              className="border-2 border-purple-300 text-purple-700 px-10 py-5 rounded-2xl font-bold text-xl hover:border-purple-400 hover:text-purple-800 hover:bg-purple-50 transition-all duration-300 flex items-center justify-center space-x-4"
            >
              <span>Explorer les projets disponibles</span>
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoVideo;